import { useState } from "react";
import { useNavigate } from "react-router-dom";
import UrlInput from "../components/UrlInput";
import { startAudit } from "../lib/api";

const FEATURES = [
  {
    title: "Crawler accessibility",
    body: "Robots.txt audit for GPTBot, ClaudeBot, PerplexityBot, Google-Extended and more.",
  },
  {
    title: "Structured data",
    body: "JSON-LD and microdata coverage, with recommended schema types AI engines look for.",
  },
  {
    title: "Content extractability",
    body: "FAQ sections, question-style headings, table of contents, passage length, freshness signals.",
  },
  {
    title: "Quick wins",
    body: "Prioritized recommendations ranked by impact and effort.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(url: string) {
    setLoading(true);
    setError(null);
    try {
      const { id } = await startAudit(url);
      navigate(`/audit/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start audit");
      setLoading(false);
    }
  }

  return (
    <div className="min-h-full bg-ink-50">
      <header className="border-b border-ink-100 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div className="text-ink-900 font-semibold tracking-tight">AEO Scanner</div>
          <a
            href="https://platform.openai.com/docs"
            target="_blank"
            rel="noreferrer"
            className="text-ink-500 hover:text-ink-900 text-sm"
          >
            What is AEO?
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <div className="space-y-6">
          <h1 className="text-ink-900 text-4xl font-semibold tracking-tight sm:text-5xl">
            See how your site looks to AI search.
          </h1>
          <p className="text-ink-500 text-lg max-w-2xl">
            Audit any URL for Answer Engine Optimization. Score how well it is set up to be
            cited by ChatGPT, Claude, Gemini and Perplexity, and get a ranked list of the
            fastest fixes.
          </p>

          <div className="pt-2">
            <UrlInput onSubmit={handleSubmit} loading={loading} />
            {error && <p className="text-red-600 mt-3 text-sm">{error}</p>}
          </div>
        </div>

        <div className="mt-16 grid gap-5 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-ink-200 bg-white p-5"
            >
              <h3 className="text-ink-900 font-medium">{f.title}</h3>
              <p className="text-ink-500 mt-1.5 text-sm">{f.body}</p>
            </div>
          ))}
        </div>

        <p className="text-ink-400 mt-10 text-sm">
          Audits typically complete in 15 to 30 seconds. Results are cached for 24 hours.
        </p>
      </main>
    </div>
  );
}
